import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Button,
  Alert,
  Tooltip,
} from "@mui/material";
import BathtubIcon from "@mui/icons-material/BathtubOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase-config"; // Adjust the import based on your project structure
import dayjs from "dayjs";

const Attendance = ({ isPrincipal, displayName, userEmail, userId }) => {
  const [classes, setClasses] = useState([]);
  const [classFilter, setClassFilter] = useState(""); // Selected class
  const [students, setStudents] = useState([]); // Students in the selected class
  const [attendance, setAttendance] = useState({}); // Attendance state
  const [attendanceDate, setAttendanceDate] = useState(dayjs()); // Default to today
  const [isDateWithinRange, setIsDateWithinRange] = useState(true);
  const [attendanceSubmitted, setAttendanceSubmitted] = useState(false);
  const [sendEmailToParents, setSendEmailToParents] = useState(true);
  const [isAllowedDate, setIsAllowedDate] = useState(true);
  const [dbAllowedDates, setDbAllowedDates] = useState([]);
  const [dbBlockedDates, setDbBlockedDates] = useState([]);
  const [allowedDateError, setAllowedDateError] = useState("");
  const [blueCardExpiringSoon, setBlueCardExpiringSoon] = useState(false);
  const [blueCardExpired, setBlueCardExpired] = useState(false);
  const [blueCardExpiryText, setBlueCardExpiryText] = useState("");

  useEffect(() => {
    if (!userId) return;
    const fetchUser = async () => {
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.blueCardExpiry) {
            const expiryDate = dayjs(userData.blueCardExpiry);
            const oneMonthFromNow = dayjs().add(1, 'month');
            if (expiryDate.isValid()) {
              setBlueCardExpiryText(expiryDate.format("DD-MM-YYYY"));
              if (expiryDate.isBefore(dayjs(), 'day')) {
                setBlueCardExpired(true);
              } else if (expiryDate.isBefore(oneMonthFromNow, 'day')) {
                setBlueCardExpiringSoon(true);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUser();
  }, [userId]);

  useEffect(() => {
    const fetchAttendanceDates = async () => {
      try {
        const docRef = doc(db, "system", "attendanceDates");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setDbAllowedDates(data.allowedDates || []);
          setDbBlockedDates(data.blockedDates || []);
        }
      } catch (error) {
        console.error("Error fetching attendance dates settings:", error);
      }
    };
    fetchAttendanceDates();
  }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "classes"));
        const classesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          grade: doc.data().grade,
        }));

        // Sort by length of `grade`, and then by `name`
        classesList.sort((a, b) => {
          if (a.grade.length === b.grade.length) {
            return a.name.localeCompare(b.name); // Alphabetical order
          }
          return a.grade.length - b.grade.length; // By grade length
        });

        setClasses(classesList);
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };

    fetchClasses();
  }, []);

  // Fetch students when the class filter changes
  useEffect(() => {
    if (!classFilter) return;

    const q = query(
      collection(db, "students"),
      where("class", "==", classFilter),
      where("status", "==", "Active"),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const studentData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort students by full name (firstName + " " + lastName)
      studentData.sort((a, b) => {
        const fullNameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const fullNameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return fullNameA.localeCompare(fullNameB); // Alphabetical order
      });

      // Initialize attendance state
      const initialAttendance = {};
      studentData.forEach((student) => {
        initialAttendance[student.regNo] = false; // Default attendance is "absent"
      });

      setStudents(studentData);
      setAttendance(initialAttendance);
    });

    return () => {
      unsubscribe();
    };
  }, [classFilter]);

  // Check if attendance record already exists
  useEffect(() => {
    const checkAttendanceRecord = async () => {
      if (!classFilter || !attendanceDate) return;

      const attendanceDocRef = doc(
        db,
        "attendance",
        `${attendanceDate.format("YYYY-MM-DD")}-${classFilter}`,
      );
      const attendanceDoc = await getDoc(attendanceDocRef);
      if (attendanceDoc.exists()) {
        setAttendanceSubmitted(true);
        setAttendance(attendanceDoc.data().records);
      } else {
        setAttendanceSubmitted(false);
        // Initialize attendance state
        const initialAttendance = {};
        students.forEach((student) => {
          initialAttendance[student.regNo] = false; // Default attendance is "absent"
        });
        setAttendance(initialAttendance);
      }
    };

    checkAttendanceRecord();
  }, [students, classFilter, attendanceDate]);

  useEffect(() => {
    const dateStr = attendanceDate.format("YYYY-MM-DD");
    const isSun = attendanceDate.day() === 0;

    const isBlockedSunday = isSun && dbBlockedDates.includes(dateStr);
    const isAllowedNonSunday = !isSun && dbAllowedDates.includes(dateStr);

    const allowed = (isSun && !isBlockedSunday) || (!isSun && isAllowedNonSunday);
    setIsAllowedDate(allowed);

    if (!allowed) {
      if (isSun) {
        setAllowedDateError("Attendance submission is blocked for this Sunday");
      } else {
        setAllowedDateError("Not Sunday");
      }
    } else {
      setAllowedDateError("");
    }

    setIsDateWithinRange(
      attendanceDate.year() === dayjs().year() &&
      !attendanceDate.isAfter(dayjs(), "day"),
    );
  }, [attendanceDate, dbAllowedDates, dbBlockedDates]);

  const handleAttendanceChange = (regNo) => {
    setAttendance((prevAttendance) => ({
      ...prevAttendance,
      [regNo]: !prevAttendance[regNo], // Toggle attendance
    }));
  };

  const submitAttendance = async () => {
    if (!classFilter) {
      alert("Please select a class.");
      return;
    }

    try {
      const attendanceDocRef = doc(
        db,
        "attendance",
        `${attendanceDate.format("YYYY-MM-DD")}-${classFilter}`,
      );
      await setDoc(attendanceDocRef, {
        date: attendanceDate.format("YYYY-MM-DD"),
        class: classFilter,
        records: attendance,
      });

      if (!attendanceSubmitted && sendEmailToParents) {
        // Send email to parents of absent students
        const absentPromises = students.map(async (student) => {
          const regNo = student.regNo;
          if (!attendance[regNo]) {
            const studentDocRef = query(
              collection(db, "students"),
              where("regNo", "==", regNo),
            );
            const studentSnapshot = await getDocs(studentDocRef);
            if (!studentSnapshot.empty) {
              const studentDoc = studentSnapshot.docs[0];
              const studentData = studentDoc.data();
              const parentEmails = [
                studentData.fatherEmail,
                studentData.motherEmail,
                studentData.guardianEmail,
              ].filter(Boolean); // Filter out any undefined or null values
              const uniqueParentEmails = [...new Set(parentEmails)]; // Remove duplicates
              const firstName = studentData.firstName || "";

              const mailDocRef = doc(collection(db, "mail"));
              await setDoc(mailDocRef, {
                to: uniqueParentEmails,
                message: {
                  subject:
                    "Absent Alert - St Marys Syro Malabar Church Sunshine Coast",
                  html: `Dear Parent,<br>Your child <b>${firstName}</b> was absent on <b>${attendanceDate.format(
                    "DD-MM-YYYY",
                  )}</b>.<br>Please notify the principals (<a href="tel:0404795214">Molly</a> or <a href="tel:0404218150">Ancy</a>) immediately if you are not aware of the absence yet. Otherwise, please ignore.<br>View total number of absences at <a href="https://catechism.stmaryssunshinecoast.org">https://catechism.stmaryssunshinecoast.org</a><br><br>Kind Regards,<br>Catechism Dept.<br>St Marys Syro Malabar Mission, Sunshine Coast`,
                },
              });
            }
          }
        });

        await Promise.all(absentPromises);
      }
      // Query users with the "principal" role
      const principalsQuery = query(
        collection(db, "users"),
        where("roles", "array-contains", "principal"),
      );
      const principalsSnapshot = await getDocs(principalsQuery);
      const principalEmails = principalsSnapshot.docs.map(
        (doc) => doc.data().email,
      );

      // Create the attendance report table
      const attendanceTableRows = students
        .map((student) => {
          const regNo = student.regNo;
          const fullName = student.firstName + " " + student.lastName;
          const isPresent = attendance[regNo] ? "P(✓)" : "A(✘)";
          return `<tr><td>${regNo}</td><td>${fullName}</td><td>${isPresent}</td></tr>`;
        })
        .join("");

      const attendanceTable = `
            <table border="1">
              <thead>
                <tr>
                  <th>Reg No</th>
                  <th>Name</th>
                  <th>Attendance</th>
                </tr>
              </thead>
              <tbody>
                ${attendanceTableRows}
              </tbody>
            </table>
          `;

      // Write a record to the mail collection for principals
      const mailDocRef = doc(collection(db, "mail"));
      await setDoc(mailDocRef, {
        to: principalEmails,
        message: {
          subject: `Attendance Submitted - ${classFilter} - ${attendanceDate.format(
            "DD-MM-YYYY",
          )}`,
          html: `Dear Principal,<br>Attendance submitted for <b>${classFilter}</b> by <a href="mailto:${userEmail}">${displayName}</a>.<br><br>${attendanceTable}<br><br>Kind Regards,<br>Catechism Dept.<br>St Marys Syro Malabar Mission, Sunshine Coast`,
        },
      });

      alert("Attendance submitted successfully!");
      setAttendanceSubmitted(true);
    } catch (error) {
      console.error("Error submitting attendance: ", error);
      alert("Failed to submit attendance.");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="sm" className="app-wrapper">
        {blueCardExpired && (
          <Alert severity="error" style={{ marginBottom: '1rem' }}>
            Your Blue Card expired on {blueCardExpiryText}! Please renew it and update your profile immediately.
          </Alert>
        )}
        {blueCardExpiringSoon && (
          <Alert severity="warning" style={{ marginBottom: '1rem' }}>
            Your Blue Card is expiring on {blueCardExpiryText}! Please renew it at <a href="https://my.bluecard.qld.gov.au/">https://my.bluecard.qld.gov.au/</a> and update your profile.
          </Alert>
        )}
        {isPrincipal ? (
          <FormControlLabel
            control={
              <Checkbox
                id="send-email-checkbox"
                checked={sendEmailToParents}
                onChange={(e) => setSendEmailToParents(e.target.checked)}
              />
            }
            label="Attendance: Send email on first Submit"
          />
        ) : (
          <Typography align="center" gutterBottom>
            Attendance
          </Typography>
        )}

        {/* Class Selector */}
        <FormControl fullWidth margin="normal" id="class-filter-form-control">
          <InputLabel id="class-filter-label">Select Class</InputLabel>
          <Select
            id="class-filter-select"
            labelId="class-filter-label"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
          >
            {classes.map((classItem) => (
              <MenuItem key={classItem.id} value={classItem.name}>
                {classItem.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Picker */}
        <FormControl fullWidth margin="normal" id="date-picker-form-control">
          <DatePicker
            id="attendance-date-picker"
            label="Select Date"
            value={attendanceDate}
            onChange={(newValue) => setAttendanceDate(newValue)}
            format="DD-MM-YYYY"
            inputFormat="YYYY-MM-DD"
            disabled={!isPrincipal}
          />
        </FormControl>
        {isDateWithinRange ? (
          isAllowedDate ? (
            classFilter &&
            attendanceSubmitted && (
              <Alert severity="warning">Attendance already submitted</Alert>
            )
          ) : (
            <Alert severity="warning">{allowedDateError}</Alert>
          )
        ) : (
          <Alert severity="warning">Invalid Date</Alert>
        )}
        <Typography variant="body1" color="error" style={{ marginTop: "1rem" }}>
          Please mark students who are <b>present</b>
        </Typography>
        {/* Student List */}
        {students.length > 0 ? (
          <List id="student-list">
            {students.map((student) => (
              <ListItem id="student-list-item" key={student.regNo} dense>
                <Tooltip id="student-list-item-tooltip" title={student.regNo}>
                  <ListItemText
                    id="student-list-item-text"
                    primary={student.firstName + " " + student.lastName}
                  />
                </Tooltip>
                {student.baptismDate &&
                  dayjs(student.baptismDate).format("MM-DD") >
                  attendanceDate.subtract(7, "day").format("MM-DD") &&
                  dayjs(student.baptismDate).format("MM-DD") <=
                  attendanceDate.format("MM-DD") && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <BathtubIcon />
                      <Typography variant="body2">
                        {student.baptismDate}
                      </Typography>
                    </div>
                  )}
                <Checkbox
                  id="student-list-item-checkbox"
                  edge="end"
                  checked={attendance[student.regNo] || false}
                  onChange={() => handleAttendanceChange(student.regNo)}
                  disabled={
                    (!isPrincipal && attendanceSubmitted) ||
                    !isAllowedDate
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          classFilter && (
            <Typography variant="body1" color="textSecondary" align="center">
              No students found for the selected class.
            </Typography>
          )
        )}

        {/* Submit Button */}
        <Button
          id="submit-attendance-button"
          variant="contained"
          color="primary"
          fullWidth
          onClick={submitAttendance}
          disabled={
            (attendanceSubmitted && !isPrincipal) ||
            !isAllowedDate ||
            !classFilter ||
            !isDateWithinRange
          }
          style={{ marginTop: "1rem" }}
        >
          Submit Attendance
        </Button>
        <Typography variant="body2" color="textSecondary">
          {!attendanceSubmitted && sendEmailToParents
            ? "Note: Email will be sent to parents whose kids are absent"
            : "Note: Email will not be sent to parents"}
        </Typography>
      </Container>
    </LocalizationProvider>
  );
};

export default Attendance;
